import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import {
  FolderOpen, LogOut, Settings, ExternalLink,
  RefreshCw, Eye, Download, ChevronDown,
} from "lucide-react";

const API        = process.env.REACT_APP_API_URL || "";
const INSPEKT_URL = process.env.REACT_APP_INSPEKT_URL || "#";
const font       = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

// ── tokens ───────────────────────────────────────────────────────────────────
const T = {
  nav:         "#232231",
  navBorder:   "#2d2c3f",
  navText:     "#9ca3af",
  navActive:   "#f9fafb",
  bg:          "#f9fafb",
  surface:     "#ffffff",
  border:      "#e5e7eb",
  text:        "#111827",
  muted:       "#6b7280",
  accent:      "#232231",
  accentLight: "#eeeef2",
  accentText:  "#232231",
  danger:      "#dc2626",
  dangerLight: "#fee2e2",
  shadow:      "0 1px 2px rgba(0,0,0,0.06)",
  shadowMd:    "0 4px 16px rgba(0,0,0,0.08)",
  r:           "8px",
  rLg:         "12px",
};


const btnGhost = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"7px 13px", fontSize:13, fontWeight:500,
  background:"transparent", color:"#232231",
  border:"1px solid #232231", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

// ── responsive hook ───────────────────────────────────────────────────────────
function useIsMobile(breakpoint = 680) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return mobile;
}

// ── avatar ────────────────────────────────────────────────────────────────────
function Avatar({ name, imageUrl, size = 28 }) {
  if (imageUrl) {
    return (
      <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0, overflow:"hidden" }}>
        <img src={imageUrl} alt="profile" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
      </div>
    );
  }
  const ch = (name?.[0] || "?").toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0,
      background:T.accent, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ color:"#fff", fontSize:size * 0.42, fontWeight:700, fontFamily:font }}>{ch}</span>
    </div>
  );
}

// ── metric strip ──────────────────────────────────────────────────────────────
function MetricStrip({ items }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display:"flex", flexWrap: isMobile ? "wrap" : "nowrap",
      background:T.surface, border:`1px solid ${T.border}`,
      borderRadius:T.rLg, overflow:"hidden", boxShadow:T.shadow, marginBottom:28,
    }}>
      {items.map((m, i) => (
        <div key={i} style={{
          flex: isMobile ? "1 1 50%" : 1,
          padding: isMobile ? "14px 16px" : "18px 22px",
          borderRight: !isMobile && i < items.length - 1 ? `1px solid ${T.border}` : "none",
          borderBottom: isMobile && i < items.length - 2 ? `1px solid ${T.border}` : "none",
          boxSizing:"border-box",
        }}>
          <p style={{ margin:0, fontSize:11, fontWeight:600, color:T.muted,
            textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:font }}>{m.label}</p>
          <p style={{ margin:"6px 0 0", fontSize: isMobile ? 22 : 26, fontWeight:700, color:T.text,
            lineHeight:1, fontFamily:font }}>{m.value ?? "—"}</p>
          {m.sub && <p style={{ margin:"4px 0 0", fontSize:12, color:T.muted, fontFamily:font }}>{m.sub}</p>}
        </div>
      ))}
    </div>
  );
}

// ── InSpekt product card ───────────────────────────────────────────────────────
function InSpektCard() {
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.rLg,
      padding:"24px", boxShadow:T.shadow, fontFamily:font }}>
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
        <div style={{ width:48, height:48, borderRadius:12, flexShrink:0,
          background:"linear-gradient(135deg,#232231,#4a4860)",
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7" stroke="white" strokeWidth="2"/>
            <circle cx="10" cy="10" r="3" fill="white"/>
          </svg>
        </div>
        <div>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.text }}>InSpekt</h3>
          <p style={{ margin:0, fontSize:13, color:T.muted }}>3D Ground Deformation Viewer</p>
        </div>
      </div>
      <p style={{ margin:"0 0 18px", fontSize:14, color:T.muted, lineHeight:1.6 }}>
        Visualise InSAR-based ground displacement data in a 3D environment with
        analysis tools — cross-section profiles, surface modelling, and point identification.
      </p>
      <a href={INSPEKT_URL} target="_blank" rel="noopener noreferrer" style={{
        display:"inline-flex", alignItems:"center", gap:8, padding:"10px 20px",
        background:`linear-gradient(135deg,${T.accent},#3d3b55)`, color:"#fff",
        borderRadius:T.r, textDecoration:"none", fontSize:14, fontWeight:600, fontFamily:font,
        boxShadow:"0 1px 4px rgba(35,34,49,0.25)",
      }}>
        Open InSpekt <ExternalLink size={13} />
      </a>
    </div>
  );
}

// ── main ──────────────────────────────────────────────────────────────────────
export default function UserDashboard({ logout, onOpenAccount }) {
  const { getToken } = useAuth();
  const { user }     = useUser();
  const isMobile            = useIsMobile();

  const [tab, setTab]               = useState("overview");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const h = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const [projects, setProjects]   = useState([]);
  const [projLoading, setPL]      = useState(false);

  const fetchProjects = useCallback(async () => {
    setPL(true);
    try {
      const token = await getToken();
      let res = await fetch(`${API}/api/getProjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // 401 means the user isn't in D1 yet (registerUser still in flight).
      // Retry once after a short delay to let it complete.
      if (res.status === 401) {
        await new Promise(r => setTimeout(r, 2500));
        res = await fetch(`${API}/api/getProjects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const d = await res.json();
      if (res.ok) setProjects(d.projects || []);
    } catch (_) {}
    finally { setPL(false); }
  }, [getToken]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const displayName = user?.firstName || user?.primaryEmailAddress?.emailAddress?.split("@")[0] || "User";
  const userEmail   = user?.primaryEmailAddress?.emailAddress || "";
  const totalReports = projects.reduce((s, p) => s + (p.reports?.length || 0), 0);

  const tabs = [
    { id:"overview",  label:"Overview" },
    { id:"projects",  label:"Projects", badge: projects.length || null },
  ];

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:font }}>

      {/* ── top nav ── */}
      <header style={{ position:"sticky", top:0, zIndex:50,
        background:T.nav, borderBottom:`1px solid ${T.navBorder}` }}>

        {/* row 1: brand + user */}
        <div style={{ maxWidth:1200, margin:"0 auto", padding:"0 16px",
          display:"flex", alignItems:"center", height:52, gap:12 }}>

          {/* brand */}
          <div style={{ display:"flex", alignItems:"center", flex:1, flexShrink:0 }}>
            <img src="/logo-white.png" alt="GeoSpekter"
              style={{ height:26, width:"auto", display:"block", flexShrink:0 }} />
          </div>

          {/* desktop tabs */}
          {!isMobile && (
            <nav style={{ display:"flex", alignItems:"center", gap:2 }}>
              {tabs.map(t => {
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    display:"flex", alignItems:"center", gap:7,
                    padding:"6px 12px", border:"none", borderRadius:6, cursor:"pointer",
                    fontFamily:font, fontSize:13, fontWeight: active ? 600 : 400,
                    background: active ? "#4d5663" : "transparent",
                    color: active ? T.navActive : T.navText,
                  }}>
                    {t.label}
                    {t.badge ? (
                      <span style={{ background: active ? T.accent : "#4d5663",
                        color:"#fff", fontSize:10, fontWeight:700, minWidth:17, height:17,
                        borderRadius:999, display:"inline-flex", alignItems:"center",
                        justifyContent:"center", padding:"0 5px" }}>
                        {t.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>
          )}

          {/* right: InSpekt + user */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            {!isMobile && (
              <a href={INSPEKT_URL} target="_blank" rel="noreferrer"
                style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12,
                  color:T.navText, textDecoration:"none",
                  padding:"5px 10px", border:"1px solid #374151", borderRadius:6 }}
                onMouseEnter={e => { e.currentTarget.style.color="#f9fafb"; e.currentTarget.style.borderColor="#6b7280"; }}
                onMouseLeave={e => { e.currentTarget.style.color=T.navText; e.currentTarget.style.borderColor="#4d5663"; }}>
                <ExternalLink size={12} /> InSpekt
              </a>
            )}

            <div style={{ position:"relative" }} ref={userMenuRef}>
              <button onClick={() => setShowUserMenu(v => !v)}
                style={{ display:"flex", alignItems:"center", gap:6, background:"none",
                  border:"none", cursor:"pointer", padding:"4px 6px", borderRadius:6 }}>
                <Avatar name={displayName} imageUrl={user?.imageUrl} size={26} />
                {!isMobile && (
                  <span style={{ fontSize:12, color:"#d1d5db", maxWidth:100,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayName}</span>
                )}
                <ChevronDown size={12} color="#6b7280" />
              </button>
              {showUserMenu && (
                <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:100,
                  background:"#fff", border:`1px solid ${T.border}`, borderRadius:T.r,
                  boxShadow:T.shadowMd, minWidth:190, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px 8px", borderBottom:`1px solid ${T.border}` }}>
                    <p style={{ margin:0, fontSize:12, fontWeight:600, color:T.text }}>{displayName}</p>
                    <p style={{ margin:0, fontSize:11, color:T.muted, marginTop:1 }}>{userEmail}</p>
                  </div>
                  {isMobile && (
                    <a href={INSPEKT_URL} target="_blank" rel="noreferrer"
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                        padding:"10px 14px", borderBottom:`1px solid ${T.border}`,
                        textDecoration:"none", fontSize:13, color:T.text, fontFamily:font }}>
                      <ExternalLink size={13} color={T.muted} /> Open InSpekt
                    </a>
                  )}
                  <button onClick={() => { setShowUserMenu(false); onOpenAccount?.(); }}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                      padding:"10px 14px", border:"none", background:"transparent",
                      cursor:"pointer", fontSize:13, color:T.text, fontFamily:font, textAlign:"left" }}>
                    <Settings size={13} color={T.muted} /> Account settings
                  </button>
                  <div style={{ height:1, background:T.border }} />
                  <button onClick={() => { setShowUserMenu(false); logout(); }}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                      padding:"10px 14px", border:"none", background:"transparent",
                      cursor:"pointer", fontSize:13, color:T.danger, fontFamily:font, textAlign:"left" }}>
                    <LogOut size={13} color={T.danger} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* row 2 (mobile): scrollable tab bar */}
        {isMobile && (
          <div style={{ borderTop:`1px solid ${T.navBorder}`, overflowX:"auto",
            WebkitOverflowScrolling:"touch", display:"flex", padding:"0 12px",
            scrollbarWidth:"none", msOverflowStyle:"none" }}>
            {tabs.map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  display:"flex", alignItems:"center", gap:6, flexShrink:0,
                  padding:"10px 12px", border:"none", background:"transparent",
                  cursor:"pointer", fontFamily:font, fontSize:13,
                  fontWeight: active ? 600 : 400,
                  color: active ? T.navActive : T.navText,
                  borderBottom: active ? `2px solid ${T.accent}` : "2px solid transparent",
                }}>
                  {t.label}
                  {t.badge ? (
                    <span style={{ background: active ? T.accent : "#4d5663",
                      color:"#fff", fontSize:10, fontWeight:700, minWidth:16, height:16,
                      borderRadius:999, display:"inline-flex", alignItems:"center",
                      justifyContent:"center", padding:"0 4px" }}>
                      {t.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/* ── content ── */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding: isMobile ? "20px 14px" : "32px 24px" }}>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            <p style={{ margin:"0 0 24px", fontSize: isMobile ? 18 : 22, fontWeight:700,
              color:T.text, fontFamily:font }}>
              Welcome back, {displayName}
            </p>

            <MetricStrip items={[
              { label:"Assigned projects", value:projects.length },
              { label:"Reports available", value:totalReports },
            ]} />

            {/* InSpekt product card */}
            <h2 style={{ margin:"0 0 14px", fontSize:14, fontWeight:600, color:T.text, fontFamily:font }}>
              Your application
            </h2>
            <div style={{ maxWidth: isMobile ? "100%" : 480, marginBottom:32 }}>
              <InSpektCard />
            </div>

            {projects.length > 0 && (
              <>
                <h2 style={{ margin:"0 0 14px", fontSize:14, fontWeight:600, color:T.text, fontFamily:font }}>
                  Recent projects
                </h2>
                <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.rLg,
                  overflow:"hidden", boxShadow:T.shadow }}>
                  <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, fontFamily:font, minWidth:300 }}>
                      <thead>
                        <tr style={{ background:"#f9fafb", borderBottom:`1px solid ${T.border}` }}>
                          {["Project", "Reports"].map(h => (
                            <th key={h} style={{ padding:"10px 16px", textAlign:"left",
                              fontSize:11, fontWeight:700, color:T.muted, letterSpacing:"0.06em",
                              textTransform:"uppercase" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {projects.slice(0, 5).map((p, i) => (
                          <tr key={p.id}
                            style={{ borderBottom: i < Math.min(projects.length, 5) - 1 ? `1px solid ${T.border}` : "none" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{ padding:"11px 16px", fontWeight:500, color:T.text }}>{p.name}</td>
                            <td style={{ padding:"11px 16px", color:T.muted }}>{p.reports?.length ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {projects.length > 5 && (
                  <button onClick={() => setTab("projects")} style={{ ...btnGhost, marginTop:12 }}>
                    View all projects
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* PROJECTS */}
        {tab === "projects" && (
          <>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
              marginBottom:20, gap:12, flexDirection: isMobile ? "column" : "row" }}>
              <div>
                <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:T.text, fontFamily:font }}>Projects</h2>
                <p style={{ margin:"3px 0 0", fontSize:13, color:T.muted, fontFamily:font }}>
                  {projects.length} project{projects.length !== 1 ? "s" : ""} assigned to your account
                </p>
              </div>
              <button onClick={fetchProjects} style={btnGhost}><RefreshCw size={13} /></button>
            </div>

            {projLoading ? (
              <p style={{ color:T.muted }}>Loading…</p>
            ) : projects.length === 0 ? (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`,
                borderRadius:T.rLg, padding:"56px 20px", textAlign:"center" }}>
                <FolderOpen size={36} color="#d1d5db" style={{ marginBottom:14 }} />
                <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>No projects yet</p>
                <p style={{ margin:"6px 0 0", fontSize:13, color:T.muted }}>
                  Contact your administrator to get access to projects.
                </p>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {projects.map(p => (
                  <div key={p.id} style={{ background:T.surface, border:`1px solid ${T.border}`,
                    borderRadius:T.rLg, padding: isMobile ? "16px" : "20px 24px", boxShadow:T.shadow }}>
                    <div style={{ display:"flex", alignItems:"flex-start",
                      justifyContent:"space-between", marginBottom: p.description || p.reports?.length ? 10 : 0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <div style={{ width:36, height:36, borderRadius:8, background:T.accentLight,
                          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <FolderOpen size={16} color={T.accent} />
                        </div>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>{p.name}</h3>
                          {p.description && (
                            <p style={{ margin:"2px 0 0", fontSize:13, color:T.muted }}>{p.description}</p>
                          )}
                        </div>
                      </div>
                      {p.reports?.length > 0 && (
                        <span style={{ fontSize:11, color:T.muted, background:"#f3f4f6",
                          padding:"3px 9px", borderRadius:999, flexShrink:0, whiteSpace:"nowrap", marginLeft:8 }}>
                          {p.reports.length} report{p.reports.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>

                    {p.reports?.length > 0 ? (
                      <div style={{ marginTop:12 }}>
                        <p style={{ margin:"0 0 8px", fontSize:11, fontWeight:700, color:T.muted,
                          textTransform:"uppercase", letterSpacing:"0.05em" }}>Reports</p>
                        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                          {p.reports.map(r => (
                            <div key={r.id} style={{ display:"flex", alignItems:"center",
                              justifyContent:"space-between", padding:"8px 12px",
                              background:"#f9fafb", borderRadius:T.r, gap:8 }}>
                              <span style={{ fontSize:13, color:T.text, flex:1, minWidth:0,
                                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {r.label || r.filename}
                              </span>
                              <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                                {!isMobile && r.created_at && (
                                  <span style={{ fontSize:11, color:T.muted }}>
                                    {new Date(r.created_at).toLocaleDateString()}
                                  </span>
                                )}
                                <button title="View" onClick={async () => {
                                  const token = await getToken();
                                  const filename = r.filename || r.label || "report.pdf";
                                  window.open(`${API}/api/report/${encodeURIComponent(filename)}?key=${encodeURIComponent(r.r2_key)}&t=${encodeURIComponent(token)}`, "_blank");
                                }} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6,
                                  cursor:"pointer", color:T.muted, padding:"4px 7px", lineHeight:0 }}>
                                  <Eye size={13} />
                                </button>
                                <button title="Download" onClick={async () => {
                                  const token = await getToken();
                                  const filename = r.filename || r.label || "report.pdf";
                                  const url = `${API}/api/report/${encodeURIComponent(filename)}?key=${encodeURIComponent(r.r2_key)}&t=${encodeURIComponent(token)}&download=1`;
                                  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
                                }} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6,
                                  cursor:"pointer", color:T.muted, padding:"4px 7px", lineHeight:0 }}>
                                  <Download size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p style={{ margin: p.description ? "8px 0 0" : 0, fontSize:13, color:"#d1d5db" }}>
                        No reports yet.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
